import { RiseOutlined } from '@ant-design/icons';
import { Card, Col, Row, Statistic, Table, type TableProps } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { PageTitle } from '@client/components/PageTitle';
import { HideColumns } from '@client/components/table/HideColumns';
import { Toolbar } from '@client/components/Toolbar';
import { EAbilitySubjects } from '@client/hooks/useAbilities';
import { useColumnsToggle } from '@client/hooks/useColumnsToggle';
import { useRevenue } from '@client/hooks/useRevenue';
import { useTable } from '@client/hooks/useTable';
import { PageLayout } from '@client/layouts/page.layout';
import { columnsMetadata } from '@client/pages/revenue/columns.metadata';
import { useLocale } from '@client/store/locale.store';
import { indexable } from '@client/utils/table/table.util';

import type { TColumns } from '@client/types/antd.type';
import type { IRevenueEntry } from '@common/types';

export const RevenuePage: React.FC = () => {
  const { t } = useTranslation();
  const { locale } = useLocale();

  const { loading, wallet, entries, total, refetch } = useRevenue();

  const {
    tableParams: { pagination },
    filteredInfo,
    sortedInfo,
    handleTableChange,
  } = useTable<IRevenueEntry>(entries, total, { syncUrl: false });

  const columns: TableProps<IRevenueEntry>['columns'] = columnsMetadata({
    locale,
    filteredInfo,
    sortedInfo,
    entities: entries,
  });

  const { filteredColumns, columnsList, selectedColumns, setSelectedColumns } =
    useColumnsToggle<IRevenueEntry>(columns as TColumns<IRevenueEntry>[], []);

  const tableProps = {
    columns: filteredColumns as TColumns<IRevenueEntry>[],
    pagination,
    scroll: { x: 1000 },
    bordered: true,
    size: 'small' as const,
    dataSource: indexable<IRevenueEntry>(entries),
    loading,
    rowKey: (record: IRevenueEntry) => record.id,
    onChange: handleTableChange,
    title: () => (
      <Toolbar<IRevenueEntry[]>
        onRefresh={refetch}
        entities={entries}
        subject={EAbilitySubjects.REVENUE}
      >
        <HideColumns
          columnsList={columnsList}
          selectedColumns={selectedColumns}
          onChange={setSelectedColumns}
        />
      </Toolbar>
    ),
    footer: () => <div>{t('table.total', { amount: total })}</div>,
  };

  return (
    <PageLayout
      subject={EAbilitySubjects.REVENUE}
      title={<PageTitle Icon={RiseOutlined} name="Revenue" />}
    >
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Available Balance"
              value={wallet?.availableBalance ?? 0}
              suffix={wallet?.currency ?? ''}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Pending Balance"
              value={wallet?.pendingBalance ?? 0}
              suffix={wallet?.currency ?? ''}
            />
          </Card>
        </Col>
      </Row>

      <Table<IRevenueEntry> {...tableProps} loading={loading} />
    </PageLayout>
  );
};
